import axios from 'axios';

import { baseUrl } from '../config/urls';
import { Ride } from '../types/ride';
import { isAndroid } from '../helpers/os';
import { nativeMessageService } from '../services/NativeMessageService';
import storageService, { StorageKeys } from '../services/StorageService';
import i18n from '../i18n';

import { apiCallDecorator, generateAndroidIdentifiers } from './helpers';

const api = axios.create({
  timeout: 90000,
});

api.defaults.baseURL = baseUrl;

const urlObject = new URL(baseUrl);
const base = `${urlObject.protocol}//${urlObject.host}`;

const markers = {
  list: () =>
    api.get(
      '/v1/markers' /* {
      params: {
        withclusters: true,
        withdrivers: true,
        radius: 40000,
      },
    } */
    ),
  create: (formData: FormData) =>
    api.post(
      '/v1/markers',
      formData /* {
      params: {
        withclusters: true,
        withdrivers: true,
        radius: 40000,
      },
    } */
    ),
  update: (id: number, formData: URLSearchParams) =>
    api.put(`/v1/markers/${id}`, formData, {
      /* params: {
        withclusters: true,
        withdrivers: true,
        radius: 40000,
      }, */
      headers: {
        'Content-Type': 'text/plain',
      },
    }),
  delete: (id: number) =>
    api.delete(
      `/v1/markers/${id}` /* , {
      params: {
        withclusters: true,
        withdrivers: true,
        radius: 40000,
      },
    } */
    ),
  setBackgroundLocation: (formData: URLSearchParams) =>
    api.put('/v1/online-markers', formData, {
      headers: {
        'Content-Type': 'text/plain',
      },
    }),
  clustersList: (lat: number, lng: number) =>
    api.get(`/v1/markers/clusters-nearby`, { params: { lat, lng, radius: 40000 } }),
};

const rides = {
  list: () => api.get('/v1/rides'),
  get: (id: number) => api.get(`/v1/rides/${id}`),
  delete: (id: number) => api.post(`/v1/rides/drop/${id}`),
  top: () => api.get('/v1/rides/top'),
  add: (formData: FormData) => api.post('/v1/rides/add', formData),
};

const auth = {
  registration: (formData: FormData) => api.post('/v1/auth', formData),
  agreed: () => api.post('/v1/auth/agreed'),
};

const settingsUrl = '/v1/filter';
const settings = {
  get: () => api.get(settingsUrl),
  set: (formData) => api.post(settingsUrl, formData),
};

const notifications = {
  subscribe: (token: string) => api.post(`/v1/system/device-token/${token}`),
};

const search = {
  set: (isSearchOn: boolean) => api.put(`/v1/search/${isSearchOn ? 'start' : 'stop'}`),
  get: () => api.get('/v1/search/status'),
};

const purchases = {
  validate: (formData: FormData) => api.post('/v1/payments', formData),
  list: (params) =>
    api.get(`/v1/payments`, {
      params,
    }),
};

const authV2 = {
  getParams: () => api.get('/v2/auth/params'),
  auth: ({
    phone_number,
    drivers_license_number,
    token_type,
    access_token,
    expires_in,
    refresh_token,
    user_id,
    extension_code,
  }) =>
    api.post('/v2/auth', {
      phone_number,
      drivers_license_number,
      token_type,
      access_token,
      expires_in,
      refresh_token,
      user_id,
      extension_code,
    }),
};

const lyftApi = {
  access_token: ({ headers, type }) => {
    const url = 'https://api.lyft.com/oauth2/access_token';
    const config = {
      headers,
    };

    const formdata = new FormData();
    formdata.append('grant_type', 'client_credentials');

    if (isAndroid) {
      nativeMessageService.sendNetworkRequestMessage({
        url,
        headers: config.headers,
        params: Object.fromEntries(formdata.entries()),
        method: 'post',
        type,
      });
      return Promise.resolve({});
    }

    return axios.post(url, formdata, {
      headers: {
        authorization: headers.authorization,
        'user-agent': headers['user-agent'],
      },
    });
  },
  phoneAuth: ({ token_type, access_token, headers, phone, type }) => {
    const url = 'https://api.lyft.com/v1/phoneauth';

    const headersCopy = {
      ...headers,
    };

    headersCopy.authorization = `${token_type} ${access_token}`;
    const config = {
      headers: headersCopy,
    };

    /* const data = {
      phone_number: `${phone}`, // из формы
      extend_token_lifetime: true,
      message_format: 'sms_basic',
    }; */

    const data = {
      phone_number: `${phone}`, // из формы
      voice_verification: false,
      message_format: 'sms_android_retriever',
      client_configuration: 'release',
    };

    if (isAndroid) {
      nativeMessageService.sendNetworkRequestMessage({
        url,
        headers: config.headers,
        params: data,
        method: 'post',
        type,
      });
      return Promise.resolve({});
    }

    return axios.post(url, data, {
      headers: {
        authorization: headersCopy.authorization,
        'user-agent': headersCopy['user-agent'],
      },
    });
  },

  token: ({ headers, phone, code, drivers_license_number = undefined, type }) => {
    const url = 'https://api.lyft.com/oauth2/access_token';
    const config = {
      headers,
    };

    const formdata = new FormData();
    formdata.append('grant_type', 'urn:lyft:oauth2:grant_type:phone');
    formdata.append('phone_number', phone);
    formdata.append('phone_code', `${code}`);

    if (isAndroid) {
      const xAccountIdentifierToken = storageService.getXAccountIdentifierToken();

      if (xAccountIdentifierToken) {
        formdata.append(
          'identifiers',
          btoa(JSON.stringify(generateAndroidIdentifiers(xAccountIdentifierToken)))
        );
      }
    }

    if (drivers_license_number) {
      formdata.append('drivers_license_number', `${drivers_license_number}`);
    }

    if (isAndroid) {
      nativeMessageService.sendNetworkRequestMessage({
        url,
        headers: config.headers,
        params: Object.fromEntries(formdata.entries()),
        method: 'post',
        type,
      });
      return Promise.resolve({});
    }

    return axios.post(url, formdata, {
      headers: {
        authorization: headers.authorization,
        'user-agent': headers['user-agent'],
      },
    });
  },
};

const getInformation = (formData: FormData) => api.post('/v1/system/helper', formData);

const sendPushNotification = (formData: FormData) =>
  api.post('/v1/system/test-push-notification', formData);

const routes = {
  get: (ride: Ride) => {
    const formData = new FormData();
    formData.append('data[service]', 'route');
    formData.append('data[data][sLat]', `${ride.start_lat}`);
    formData.append('data[data][sLng]', `${ride.start_lng}`);
    formData.append('data[data][fLat]', `${ride.end_lat}`);
    formData.append('data[data][fLng]', `${ride.end_lng}`);
    formData.append('data[data][params][overview]', 'full');
    formData.append('data[data][params][geometries]', 'geojson');
    formData.append('data[data][params][alternatives]', 'false');

    return getInformation(formData);
  },
};

const worker = {
  log: (data: object) =>
    api.post(
      `${base}/api/worker/v1/log`,
      { data },
      {
        baseURL: base,
      }
    ),
};

const apiList = [authV2, auth, settings, search, markers, rides, notifications, purchases, worker];

apiList.forEach((apiSection) => {
  Object.keys(apiSection).forEach((method) => {
    apiSection[method] = apiCallDecorator(apiSection[method]);
  });
});

api.interceptors.request.use((config) => {
  const clientHeaders = storageService.getItem(StorageKeys.clientHeaders);

  config.headers['X-Language'] = `${i18n.language}-US`;

  if (clientHeaders) {
    try {
      const clientHeadersObject = JSON.parse(clientHeaders);
      config.headers = {
        ...config.headers,
        ...clientHeadersObject,
      };
    } catch (e) {
      console.log(e);
    }
  }

  return config;
});

api.interceptors.request.use((config) => {
  (async () => {
    const { Authorization } = config.headers;

    if (!Authorization) {
      return;
    }

    const notificationAuthToken = storageService.getItem(StorageKeys.notificationAuthToken);
    const deviceToken = storageService.getItem(StorageKeys.deviceToken);
    const token = `${Authorization}-${deviceToken}`;

    if (
      deviceToken &&
      deviceToken.length > 10 &&
      (!notificationAuthToken || notificationAuthToken !== token)
    ) {
      try {
        storageService.setItem(StorageKeys.notificationAuthToken, token);
        const response = await notifications.subscribe(deviceToken);

        // @ts-ignore
        if (response.code >= 300) {
          storageService.removeItem(StorageKeys.notificationAuthToken);
        }
      } catch (e) {
        storageService.removeItem(StorageKeys.notificationAuthToken);
        console.log(e);
      }
    }
  })();

  return config;
});

export default {
  api,
  auth,
  authV2,
  settings,
  search,
  markers,
  rides,
  notifications,
  getInformation,
  sendPushNotification,
  purchases,
  routes,
  lyftApi,
  worker,
};
